import { useMemo, useState } from "react"
import { Edit, MoreHorizontal, Plus, Trash } from "react-feather"
import { permissionApi } from "@/api/client/permission"
import { roleApi } from "@/api/client/role"
import type { RoleList200RolesItem } from "@/api/generated/models"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { SectionError } from "@/components/ui/error"
import { SectionLoading } from "@/components/ui/loading"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { useOrganizationOptions } from "@/features/organization"
import { DeleteRoleModal, RoleFormModal } from "./components"

export function RolesPage() {
  const { canAccess, canCreate, canUpdate, canDelete, refetch: refetchPermission } = usePermission()
  const canCreateRole = canCreate("roles")
  const canReadOrganizations = canAccess("organizations")

  const {
    data: roleData,
    isPending: isRolePending,
    isError: isRoleError,
    refetch: refetchRoles,
  } = roleApi.useRoleList()
  const {
    data: permissionData,
    isPending: isPermissionPending,
    isError: isPermissionError,
    refetch: refetchPermissions,
  } = permissionApi.usePermissionList()
  const {
    options: organizationOptions,
    isPending: isOrganizationPending,
    isError: isOrganizationError,
    refetch: refetchOrganizations,
  } = useOrganizationOptions({ enabled: canCreateRole && canReadOrganizations })

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<RoleList200RolesItem | null>(null)
  const [roleToDelete, setRoleToDelete] = useState<RoleList200RolesItem | null>(null)

  const resources = useMemo(() => permissionData?.resources ?? [], [permissionData])
  const roles = roleData?.roles ?? []

  const handleOpenAddModal = () => {
    setSelectedRole(null)
    setIsFormModalOpen(true)
  }

  const handleOpenEditModal = (role: RoleList200RolesItem) => {
    setSelectedRole(role)
    setIsFormModalOpen(true)
  }

  // 自分のロールを編集した場合にメニューや操作可否へ即時反映する
  const handleRefetch = () => {
    refetchRoles()
    refetchPermission()
  }

  if (isRolePending || isPermissionPending) {
    return <SectionLoading />
  }

  if (isRoleError || isPermissionError) {
    return (
      <SectionError
        message="ロール情報を取得できませんでした。権限が不足しているか、通信に失敗した可能性があります。"
        onRetry={() => {
          refetchRoles()
          refetchPermissions()
        }}
      />
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">ロール管理</h1>

      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{roles.length}件のロールが見つかりました</div>
        {canCreateRole && (
          <div className="flex flex-col items-end gap-1">
            <Button
              onClick={handleOpenAddModal}
              disabled={
                !canReadOrganizations ||
                isOrganizationPending ||
                isOrganizationError ||
                organizationOptions.length === 0
              }
              title={
                canReadOrganizations ? undefined : "ロールの追加には組織管理の閲覧権限が必要です"
              }
            >
              <Plus className="mr-2" size={16} />
              ロールを追加
            </Button>
            {!canReadOrganizations && (
              <p className="text-xs text-slate-500">追加には組織管理の閲覧権限が必要です</p>
            )}
            {isOrganizationError && (
              <div className="flex items-center gap-2 text-xs text-red-600">
                <span>組織一覧を取得できませんでした。</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => refetchOrganizations()}
                >
                  再読み込み
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">ロール名</TableHead>
              <TableHead className="whitespace-nowrap">表示名</TableHead>
              <TableHead className="whitespace-nowrap">組織</TableHead>
              <TableHead className="whitespace-nowrap">権限のある画面</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={`${role.organizationId ?? ""}:${role.role}`}>
                <TableCell>{role.role}</TableCell>
                <TableCell
                  className={
                    role.editable && canUpdate("roles")
                      ? "text-blue-600 cursor-pointer hover:underline"
                      : ""
                  }
                  onClick={
                    role.editable && canUpdate("roles")
                      ? () => handleOpenEditModal(role)
                      : undefined
                  }
                >
                  {role.label}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {role.organizationName ?? <span className="text-slate-500">全組織</span>}
                </TableCell>
                <TableCell>
                  {resources
                    .filter((resource) => (role.permissions[resource.resource] ?? []).length > 0)
                    .map((resource) => resource.label)
                    .join("、") || "-"}
                </TableCell>
                <TableCell>
                  {role.editable && (canUpdate("roles") || canDelete("roles")) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[120px]">
                        {canUpdate("roles") && (
                          <DropdownMenuItem onClick={() => handleOpenEditModal(role)}>
                            <Edit className="text-slate-600 mr-2" size={16} />
                            編集
                          </DropdownMenuItem>
                        )}
                        {canUpdate("roles") && canDelete("roles") && <DropdownMenuSeparator />}
                        {canDelete("roles") && (
                          <DropdownMenuItem
                            onClick={() => setRoleToDelete(role)}
                            className="text-red-600"
                          >
                            <Trash className="text-red-400 mr-2" size={16} />
                            削除
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  ロールが見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <RoleFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        role={selectedRole}
        resources={resources}
        organizationOptions={organizationOptions}
        onRefetch={handleRefetch}
      />

      <DeleteRoleModal
        isOpen={roleToDelete !== null}
        onClose={() => setRoleToDelete(null)}
        role={roleToDelete}
        onRefetch={handleRefetch}
      />
    </div>
  )
}
